import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  asapScheduler,
  concatMap,
  first,
  mergeMap,
  observeOn,
  ReplaySubject,
  Subject,
  Subscription,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import {
  getToolMapActionLabelDictionary,
  getToolMapLabelDictionary,
  getWidgetMapLabelDictionary,
  getWidgetModalLabelDictionary,
  getWidgetModalLogLabelDictionary,
} from '../constants/widget.constant';
import { ToolMapActionType } from '../models/tool-action-type';
import { ToolMapType } from '../models/tool-map-type';
import { WidgetModalType } from '../models/widget-modal-type';
import { WidgetType } from '../models/widget-type';
import { AppConfigService } from './app-config.service';
import { AuthService } from './auth.service';
import { TranslationService } from './translation.service';
import { Analytics, AnalyticsInstance } from 'analytics';
import googleAnalytics from '@analytics/google-analytics';
import { environment } from '@environments';

interface TrackingLogModelLike {
  type: TrackingToolType;
  name?: string;
  action: boolean;
}

export enum TrackingToolType {
  AppAccess = 'A',
  Layer = 'C',
  Widget = 'W',
}

const cookieKey = 'Cookie-analytics';

@Injectable()
export class AppTrackingService implements OnDestroy {
  private analyticsConsent = false;
  private isTrackGoogleLoaded = false;
  private isTrackGoogleCanceled = false;
  private _isRequestPending = false;

  get isRequestPendingGoogle() {
    return this._isRequestPending;
  }

  private _trackGoogleLoadedSubject = new ReplaySubject<AnalyticsInstance>(1);
  private _trackGoogleSubject = new Subject<any>();
  private _trackGoogleSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private appConfig: AppConfigService,
    private translationService: TranslationService
  ) {
    if (this.appConfig.externalMode) {
      this._loadGoogleIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this._trackGoogleSubscription?.unsubscribe();
  }

  sendAppAccess() {
    this.send({
      type: TrackingToolType.AppAccess,
      action: true,
    });
  }

  sendWidgetModal(type: WidgetModalType, action = true) {
    const name =
      getWidgetModalLogLabelDictionary(this.translationService).get(type) ??
      getWidgetModalLabelDictionary(this.translationService).get(type);

    this.send({
      type: TrackingToolType.Widget,
      name,
      action,
    });
  }

  sendWidgetMap(type: WidgetType, action = true) {
    const name = getWidgetMapLabelDictionary(this.translationService).get(type);

    this.send({
      type: TrackingToolType.Widget,
      name,
      action,
    });
  }

  sendToolMap(type: ToolMapType, action = true) {
    const name = getToolMapLabelDictionary(this.translationService).get(type);

    this.send({
      type: TrackingToolType.Widget,
      name,
      action,
    });
  }

  sendToolMapAction(type: ToolMapActionType, action = true) {
    const name = getToolMapActionLabelDictionary(this.translationService).get(type);

    this.send({
      type: TrackingToolType.Widget,
      name,
      action,
    });
  }

  sendLayer(layerName: string, visible: boolean) {
    this.send({
      type: TrackingToolType.Layer,
      name: layerName,
      action: visible,
    });
  }

  send(model: TrackingLogModelLike) {
    let application: string;
    if (this.appConfig.siiaMode) {
      application = 'SIIA';
    } else if (this.appConfig.internalMode) {
      application = 'INTERNO';
    } else if (this.appConfig.externalMode) {
      application = 'EXTERNO';
    }

    const body: any = Object.assign({}, model, { application });
    body.action = body.action ? 'T' : 'F';

    zip(this.authService.getUserId(), this.authService.getUserName())
      .pipe(
        mergeMap(([userId, userName]) => {
          body.idUser = userId;
          body.userName = userName;

          this._trackGoogle(body);

          return this.http.post(`~Log`, body);
        })
      )
      .subscribe();
  }

  setAcceptCookies(accepted: boolean) {
    // const expires = Date.now() + 10 * 1000; // 30 dias
    const expires = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 dias

    globalThis.cookieStore.set({
      name: 'Cookie-necessary',
      value: true,
      expires,
      path: '/',
    });
    globalThis.cookieStore.set({
      name: cookieKey,
      value: !!accepted,
      expires,
      path: '/',
    });

    this._loadGoogleIfNeeded();
  }

  private async hasAcceptCookies(): Promise<boolean> {
    const item = await this.getAcceptCookies();
    return !!item;
  }

  private async getAcceptCookiesValue(): Promise<boolean> {
    const item = await this.getAcceptCookies();
    return String(item?.value ?? 'false') === 'true';
  }

  private async getAcceptCookies(): Promise<any> {
    const item = await globalThis.cookieStore.get(cookieKey);
    return item;
  }

  private _trackGoogle(event: any) {
    this._trackGoogleSubject.next(event);
  }

  private async _loadGoogleIfNeeded() {
    this._isRequestPending = !(await this.hasAcceptCookies());

    if (!this._isRequestPending) {
      this.analyticsConsent = await this.getAcceptCookiesValue();
    }

    if (this.isTrackGoogleLoaded || this.isTrackGoogleCanceled) {
      return;
    }

    this._setupGoogleTrackEvents();

    const trackingId = this.appConfig.googleAnalyticsId;

    const analytics = Analytics({
      app: this.appConfig.appName,
      version: 100,
      debug: !environment.production,
      plugins: [
        googleAnalytics({
          measurementIds: [trackingId],
        }),
      ],
    });

    this.isTrackGoogleLoaded = true;

    analytics.ready(() => {
      analytics.page();

      this._trackGoogleLoadedSubject.next(analytics);
    });
  }

  private _setupGoogleTrackEvents() {
    if (this._trackGoogleSubscription) {
      return;
    }

    const { _trackGoogleLoadedSubject, _trackGoogleSubject } = this;

    const cancelLoadObservable = _trackGoogleLoadedSubject.pipe(
      first((x) => !x)
    );

    const loadObservable = _trackGoogleLoadedSubject.pipe(first((x) => !!x));

    this._trackGoogleSubscription = _trackGoogleSubject
      .pipe(
        concatMap((event) => {
          return loadObservable.pipe(
            observeOn(asapScheduler),
            tap((analytics) => {
              analytics.track('customTracking', event);
            })
          );
        }),
        takeUntil(cancelLoadObservable)
      )
      .subscribe();
  }
}
